import { isNaN, isNil } from 'lodash';
import {
  CURRENCY_MAP,
  CURRENCY_RIGHT_HAND_SYMBOL,
  CURRENCY_SHOW_ISO_CODE,
} from './currencies';

export interface FormatCurrencyOptions {
  showPlus?: boolean;
  showDecimal?: boolean;
  showZeroDecimals?: boolean;
  isMasked?: boolean;
  showIsoCode?: boolean;
}

interface CurrencyParts {
  sign?: string;
  leftSymbol?: string;
  rightSymbol?: string;
  integer?: string;
  decimal?: string;
  isoCode?: string;
}

export const getCurrencySymbol = (currency?: string | null): string => {
  if (!currency) {
    return '';
  }
  return CURRENCY_MAP[currency as keyof typeof CURRENCY_MAP] ?? '';
};

export const getCurrencyParts = (
  value?: number | string | null,
  currency?: string | null,
  {
    showPlus,
    showDecimal = true,
    showZeroDecimals = true,
    isMasked = false,
    showIsoCode = true,
  }: FormatCurrencyOptions = {}
): CurrencyParts => {
  if (isNil(value) || isNaN(value)) {
    return {};
  }
  const symbol = getCurrencySymbol(currency);
  const hasRightHandSymbol =
    currency && CURRENCY_RIGHT_HAND_SYMBOL.includes(currency);
  const hasIsoCode =
    currency && (CURRENCY_SHOW_ISO_CODE.includes(currency) || !symbol);

  const numericValue = Number.parseFloat(value.toString());
  const plusSign = showPlus && numericValue > 0 ? '+ ' : '';
  const formattedAmount = isMasked
    ? '***.**'
    : numericValue
        .toLocaleString(undefined, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
        .replace(/^-/, ''); // Remove sign, to be added later

  return {
    sign: numericValue < 0 ? '- ' : plusSign,
    leftSymbol: hasRightHandSymbol ? '' : symbol,
    rightSymbol: hasRightHandSymbol ? symbol : '',
    integer: formattedAmount.slice(0, -3),
    decimal: getDecimal(formattedAmount, {
      showDecimal,
      showZeroDecimals,
    }),
    isoCode: showIsoCode && hasIsoCode ? ` ${currency}` : '',
  };
};

export const formatCurrency = (
  value?: number | string | null,
  currency?: string | null,
  options: FormatCurrencyOptions = {}
) => {
  if (isNil(value) || isNaN(value)) {
    return '';
  }

  const { sign, leftSymbol, integer, decimal, rightSymbol, isoCode } =
    getCurrencyParts(value, currency, options);

  return `${sign}${leftSymbol}${integer}${decimal}${rightSymbol}${isoCode}`;
};

const getDecimal = (
  formattedAmount: string,
  { showDecimal = true, showZeroDecimals }: FormatCurrencyOptions
) => {
  if (!showDecimal) {
    return '';
  }
  const decimal = formattedAmount.slice(-3);
  if (!showZeroDecimals && decimal === '.00') {
    return '';
  }
  return decimal;
};
